<template>
  <div class="about-page">
    <!-- Banner Section -->
    <section class="banner-section">
      <div class="banner-content">
        <h1 class="banner-title">{{ $t('aboutPage.banner.title') }}</h1>
      </div>
    </section>

    <!-- About Us Section -->
    <section class="about-section">
      <div class="container">
        <h2 class="section-title">{{ $t('aboutPage.about.title') }}</h2>
        <div class="about-content">
          <div class="about-image-wrapper">
            <img src="@/assets/about/about.png"
                 :alt="$t('aboutPage.images.aboutAlt')"
                 class="about-image">
          </div>
          <div class="about-text-card">
            <div class="card-content">
              <p>{{ $t('aboutPage.about.body') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us Section -->
    <section class="intro-section">
      <div class="container flex-container">
        <div class="intro-text-area">
          <h2 class="intro-title">{{ $t('aboutPage.intro.title') }}</h2>
          <p class="intro-desc">
            {{ $t('aboutPage.intro.desc') }}
          </p>

          <div class="feature-list">
            <div class="feature-item">
              <div class="icon-wrapper">
                <img src="@/assets/about/chooseicon1.png"
                     :alt="$t('aboutPage.images.featureIcon1Alt')"
                     class="feature-icon">
              </div>
              <div class="feature-text">
                {{ $t('aboutPage.intro.features.projectLearning') }}
              </div>
            </div>
            <div class="feature-item">
              <div class="icon-wrapper">
                <img src="@/assets/about/chooseicon2.png"
                     :alt="$t('aboutPage.images.featureIcon2Alt')"
                     class="feature-icon">
              </div>
              <div class="feature-text">
                {{ $t('aboutPage.intro.features.interactiveExperiments') }}
              </div>
            </div>
          </div>
        </div>

        <div class="intro-image-wrapper">
          <img src="@/assets/about/choose.png"
               :alt="$t('aboutPage.images.introAlt')"
               class="intro-image">
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { buildMetaInfo } from '../seo/buildMetaInfo'

export default {
  name: 'AboutPage',
  metaInfo() {
    const seoRecord = this.$store.getters['seo/getSeoByPathAndLocale'](
      this.$route.path,
      this.$i18n.locale
    )
    return buildMetaInfo({
      seoRecord,
      fallbackTitle: this.$t('aboutPage.seoTitle'),
    })
  }
}
</script>

<style scoped>
.about-page {
  width: 100%;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  margin: 0;
  padding: 0;
}

/* Banner Section */
.banner-section {
  width: 100%;
  height: 546px;
  margin-top: -10px;
  background-image: url("@/assets/about/banner.png");
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
}

.banner-content {
  text-align: center;
}

.banner-title {
  font-size: 48px;
  font-weight: bold;
  color: #fff;
  margin: 0;
}

/* About Us Section */
.about-section {
  width: 100%;
  height: 811px;
  background-color: #fff;
  display: flex;
  align-items: center; /* Center content vertically in the section */
}

.container {
  width: 1200px;
  margin: 0 auto;
}

.section-title {
  font-size: 34px;
  color: #333;
  font-weight: bold;
  text-align: center;
  margin-bottom: 60px;
}

.about-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.about-image-wrapper {
  width: 532px;
  height: 365px;
  flex-shrink: 0;
  margin-top: 80px;
}

.about-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.about-text-card {
  width: 649px;
  height: 496px;
  background-color: #fff;
  border-radius: 12px;

  border: 1px solid #e0e0e0; /* Subtle border for the card itself */
  border-left: 44px solid #2ea2fa; /* The 44px blue border */
  box-sizing: border-box;
  padding: 40px;
  display: flex;
  align-items: center;
}

.card-content p {
  font-size: 14px;
  color: #666;
  line-height: 2;
  text-align: justify;
}

/* Introduction Section */
.intro-section {
  width: 100%;
  height: 662px;
  background-color: #f4f6fa;
  display: flex;
  align-items: center;
}

.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.intro-text-area {
  width: 600px;
  height: 348px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
}

.intro-title {
  font-size: 34px;
  color: #333;
  font-weight: bold;
  margin-bottom: 20px;
}

.intro-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 30px;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
}

.icon-wrapper {
  width: 51px;
  height: 51px;
  background-color: #ffd700; /* Placeholder color, maybe yellow based on image */
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  flex-shrink: 0;
}

/* Adjust icon style based on the second item usually being green in such designs, but for now simple style */
.feature-item:nth-child(2) .icon-wrapper {
  background-color: #76c043;
}

.feature-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.feature-text {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  padding-top: 0; /* Align with icon top if needed, or center */
  display: flex;
  align-items: center;
  min-height: 51px;
}

.intro-image-wrapper {
  width: 550px;
  height: 415px;
}

.intro-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}
</style>
